/**
 * 分支HTTP API服务
 *
 * 封装分支相关的REST API调用，用于分支CRUD操作。
 * 使用 HttpOnly Cookie 进行认证，浏览器自动携带 Token。
 */

import type {
  Branch,
  BranchTreeNode,
  BranchCreate,
  SingleBranchCreate,
  BranchUpdate,
  BranchStats,
  BranchSwitchResult,
} from '../types/branch';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

export class HttpError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string
  ) {
    super(message);
    this.name = 'HttpError';
  }
}

export class UnauthorizedError extends HttpError {
  constructor(message: string = '未授权，请重新登录') {
    super(message, 401, 'UNAUTHORIZED');
    this.name = 'UnauthorizedError';
  }
}

export class NotFoundError extends HttpError {
  constructor(message: string = '资源不存在') {
    super(message, 404, 'NOT_FOUND');
    this.name = 'NotFoundError';
  }
}

export class ForbiddenError extends HttpError {
  constructor(message: string = '没有权限执行此操作') {
    super(message, 403, 'FORBIDDEN');
    this.name = 'ForbiddenError';
  }
}

interface BranchListResponse {
  branches: Branch[];
  total: number;
}

interface BranchTreeResponse {
  tree: BranchTreeNode[];
}

function getHeaders(): HeadersInit {
  return {
    'Content-Type': 'application/json',
  };
}

async function handleResponseError(response: Response): Promise<never> {
  let errorMessage = '请求失败';

  try {
    const data = await response.json();
    errorMessage = data.detail || data.message || errorMessage;
  } catch {
  }

  switch (response.status) {
    case 401:
      throw new UnauthorizedError(errorMessage);
    case 403:
      throw new ForbiddenError(errorMessage);
    case 404:
      throw new NotFoundError(errorMessage);
    default:
      throw new HttpError(errorMessage, response.status);
  }
}

export class BranchApiService {
  private baseUrl: string;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  async listBranches(workspaceId: number): Promise<Branch[]> {
    const response = await fetch(
      `${this.baseUrl}/api/branches/workspaces/${workspaceId}`,
      {
        method: 'GET',
        headers: getHeaders(),
        credentials: 'include',
      }
    );

    if (!response.ok) {
      await handleResponseError(response);
    }

    const data: BranchListResponse = await response.json();
    return data.branches;
  }

  async getBranchTree(workspaceId: number): Promise<BranchTreeNode[]> {
    const response = await fetch(
      `${this.baseUrl}/api/branches/workspaces/${workspaceId}/tree`,
      {
        method: 'GET',
        headers: getHeaders(),
        credentials: 'include',
      }
    );

    if (!response.ok) {
      await handleResponseError(response);
    }

    const data: BranchTreeResponse = await response.json();
    return data.tree;
  }

  async getBranch(branchId: number): Promise<Branch> {
    const response = await fetch(
      `${this.baseUrl}/api/branches/${branchId}`,
      {
        method: 'GET',
        headers: getHeaders(),
        credentials: 'include',
      }
    );

    if (!response.ok) {
      await handleResponseError(response);
    }

    return await response.json();
  }

  async getBranchStats(branchId: number): Promise<BranchStats> {
    const response = await fetch(
      `${this.baseUrl}/api/branches/${branchId}/stats`,
      {
        method: 'GET',
        headers: getHeaders(),
        credentials: 'include',
      }
    );

    if (!response.ok) {
      await handleResponseError(response);
    }

    return await response.json();
  }

  async switchBranch(workspaceId: number, branchId: number | null): Promise<BranchSwitchResult> {
    const url = `${this.baseUrl}/api/branches/workspaces/${workspaceId}/switch`;
    const params = branchId !== null ? `?branch_id=${branchId}` : '';

    const response = await fetch(`${url}${params}`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
    });

    if (!response.ok) {
      await handleResponseError(response);
    }

    const data = await response.json();
    return {
      current_branch: data.current_branch,
      is_main_line: data.is_main_line,
      chapters: data.chapters,
    };
  }

  async createBranchPoint(data: BranchCreate): Promise<Branch[]> {
    const response = await fetch(
      `${this.baseUrl}/api/branches`,
      {
        method: 'POST',
        headers: getHeaders(),
        credentials: 'include',
        body: JSON.stringify(data),
      }
    );

    if (!response.ok) {
      await handleResponseError(response);
    }

    const result: BranchListResponse = await response.json();
    return result.branches;
  }

  async createSingleBranch(data: SingleBranchCreate): Promise<Branch> {
    const response = await fetch(
      `${this.baseUrl}/api/branches/single`,
      {
        method: 'POST',
        headers: getHeaders(),
        credentials: 'include',
        body: JSON.stringify(data),
      }
    );

    if (!response.ok) {
      await handleResponseError(response);
    }

    return await response.json();
  }

  async updateBranch(branchId: number, data: BranchUpdate): Promise<Branch> {
    const response = await fetch(
      `${this.baseUrl}/api/branches/${branchId}`,
      {
        method: 'PUT',
        headers: getHeaders(),
        credentials: 'include',
        body: JSON.stringify(data),
      }
    );

    if (!response.ok) {
      await handleResponseError(response);
    }

    return await response.json();
  }

  async deleteBranch(branchId: number, branchName: string): Promise<void> {
    const response = await fetch(
      `${this.baseUrl}/api/branches/${branchId}`,
      {
        method: 'DELETE',
        headers: getHeaders(),
        credentials: 'include',
        body: JSON.stringify({ branch_name: branchName }),
      }
    );

    if (!response.ok) {
      await handleResponseError(response);
    }
  }

  async reorderBranches(
    workspaceId: number,
    branches: Array<{ id: number; sort_order: number }>
  ): Promise<void> {
    const response = await fetch(
      `${this.baseUrl}/api/branches/reorder`,
      {
        method: 'POST',
        headers: getHeaders(),
        credentials: 'include',
        body: JSON.stringify({ workspace_id: workspaceId, branches }),
      }
    );

    if (!response.ok) {
      await handleResponseError(response);
    }
  }
}

export const branchApi = new BranchApiService();

export default branchApi;